import { LOANLIGHT_EVENTS } from "../constants/events";

/**
 * Creates a standardized CustomEvent with consistent options
 * @param eventName - The name of the event
 * @param detail - Event detail data
 * @param options - Additional event options
 * @returns CustomEvent instance
 */
export function createLoanLightEvent<T = unknown>(
  eventName: string,
  detail: T,
  options: {
    bubbles?: boolean;
    composed?: boolean;
    cancelable?: boolean;
  } = {},
): CustomEvent<T> {
  return new CustomEvent(eventName, {
    detail,
    bubbles: options.bubbles ?? true,
    composed: options.composed ?? true,
    cancelable: options.cancelable ?? false,
  });
}

/**
 * Dispatches a LoanLight event with consistent options
 * @param element - The element to dispatch the event from
 * @param eventName - The name of the event
 * @param detail - Event detail data
 * @param options - Additional event options
 */
export function dispatchLoanLightEvent<T = unknown>(
  element: Element,
  eventName: string,
  detail: T,
  options: {
    bubbles?: boolean;
    composed?: boolean;
    cancelable?: boolean;
  } = {},
): void {
  const event = createLoanLightEvent(eventName, detail, options);
  element.dispatchEvent(event);
}

/**
 * Adds an event listener with proper TypeScript typing
 * @param element - The element to add the listener to
 * @param eventName - The event name from LOANLIGHT_EVENTS
 * @param listener - The event listener function
 * @param options - Event listener options
 */
export function addLoanLightEventListener<
  K extends keyof typeof LOANLIGHT_EVENTS,
>(
  element: Element,
  eventName: K,
  listener: (event: CustomEvent) => void,
  options?: AddEventListenerOptions,
): void {
  element.addEventListener(
    LOANLIGHT_EVENTS[eventName],
    listener as EventListener,
    options,
  );
}

/**
 * Removes an event listener
 * @param element - The element to remove the listener from
 * @param eventName - The event name from LOANLIGHT_EVENTS
 * @param listener - The event listener function
 * @param options - Event listener options
 */
export function removeLoanLightEventListener<
  K extends keyof typeof LOANLIGHT_EVENTS,
>(
  element: Element,
  eventName: K,
  listener: (event: CustomEvent) => void,
  options?: EventListenerOptions,
): void {
  element.removeEventListener(
    LOANLIGHT_EVENTS[eventName],
    listener as EventListener,
    options,
  );
}

/**
 * Event handler decorator for Lit components
 * Provides consistent event handling with error boundaries
 * @param handler - The event handler function
 * @returns Wrapped handler with error handling
 */
export function withEventErrorHandling<Args extends unknown[], R>(
  handler: (...args: Args) => R,
): (...args: Args) => R {
  const getTargetElement = (value: unknown): Element | null => {
    if (!value || typeof value !== "object" || !("target" in value)) {
      return null;
    }

    const target = (value as { target?: unknown }).target;
    return target instanceof Element ? target : null;
  };

  return ((...args: Args) => {
    try {
      return handler(...args);
    } catch (error) {
      if (
        typeof process !== "undefined" &&
        process.env?.NEXT_PUBLIC_NODE_ENV === "development"
      ) {
        console.error(`Event handler error:`, error);
      }

      const element = getTargetElement(args[0]);
      if (element) {
        dispatchLoanLightEvent(element, LOANLIGHT_EVENTS.LOANLIGHT_ERROR, {
          error: error instanceof Error ? error.message : String(error),
          handler: handler.name,
        });
      }
    }
  }) as (...args: Args) => R;
}

export class EventListenerManager {
  private element: Element;
  private listeners: Map<string, EventListener> = new Map();

  constructor(element: Element) {
    this.element = element;
  }

  add<K extends keyof typeof LOANLIGHT_EVENTS>(
    eventName: K,
    listener: (event: CustomEvent) => void,
    options?: AddEventListenerOptions,
  ): void {
    const key = `${eventName}-${listener.name || "anonymous"}`;

    this.remove(eventName, listener);

    addLoanLightEventListener(this.element, eventName, listener, options);
    this.listeners.set(key, listener as EventListener);
  }

  remove<K extends keyof typeof LOANLIGHT_EVENTS>(
    eventName: K,
    listener: (event: CustomEvent) => void,
  ): void {
    const key = `${eventName}-${listener.name || "anonymous"}`;
    const existingListener = this.listeners.get(key);

    if (existingListener) {
      removeLoanLightEventListener(this.element, eventName, listener);
      this.listeners.delete(key);
    }
  }

  removeAll(): void {
    for (const [key, listener] of this.listeners) {
      const [eventName] = key.split("-");
      removeLoanLightEventListener(
        this.element,
        eventName as keyof typeof LOANLIGHT_EVENTS,
        listener as (event: CustomEvent) => void,
      );
    }
    this.listeners.clear();
  }

  get listenerCount(): number {
    return this.listeners.size;
  }
}
